<template>
  <transition name="fading">
    <Loading v-if="isLoading"/>
  </transition>

  <AddClinics
    :isActive="isAddClinics"
    @modal-close="close_modal()"
    @modal-submit="submit_modal()"
  />

  <div class="admin-page wide">
    <div class="page-hero">
      <div>
        <p class="page-kicker">Location Registry</p>
        <h1 class="page-title">Clinics / Hospitals</h1>
        <p class="page-subtitle">
          Each clinic or hospital is a physical service location and should belong to an organization.
        </p>
      </div>
      <button class="ui-button primary" @click="isAddClinics = true">
        <i class="fa-solid fa-plus"></i> Create location
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <span class="stat-label">Locations</span>
        <strong class="stat-value">{{ raw_data.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Filtered</span>
        <strong class="stat-value">{{ render_data.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Missing org</span>
        <strong class="stat-value">{{ unassignedCount }}</strong>
      </div>
    </div>

    <div class="toolbar">
      <div class="toolbar-search">
        <SearchBar
          searchPlaceholder="Search clinic, hospital, city, state, organization..."
          buttonPlaceholder="Search"
          @OutputSearch="get_search_query"
        />
      </div>
      <button class="ui-button secondary" @click="$router.push('/bulk-setup')" v-if="authStore.userRole === 'admin'">
        <i class="fas fa-layer-group"></i> Bulk setup
      </button>
    </div>

    <div class="table-card">
      <table class="ui-table mobile-card-table">
        <thead>
          <tr>
            <th class="text-start">Clinic / Hospital</th>
            <th class="text-start">Organization</th>
            <th class="text-start">Address</th>
            <th class="text-start">Last Updated</th>
            <th class="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="data in paginatedData" :key="data.LocationUuid" @click="goto_editor(data.LocationUuid)" class="clickable-row">
            <td data-label="Clinic">
              <span class="table-primary">{{ data.ClinicName || 'Unnamed location' }}</span>
              <span class="table-secondary">Clinic / hospital location</span>
            </td>
            <td data-label="Organization">
              <span class="badge-soft" :class="data.OrganizationUuid ? 'success' : 'danger'">
                {{ organizationName(data.OrganizationUuid) }}
              </span>
            </td>
            <td data-label="Address">
              <span class="table-primary">{{ [data.City, data.State].filter(Boolean).join(', ') || 'No city/state' }}</span>
              <span class="table-secondary">{{ data.Address || 'No address yet' }}</span>
            </td>
            <td data-label="Updated">
              <span class="table-primary">{{ formatDate(data.UpdatedAt) }}</span>
            </td>
            <td class="text-end" data-label="Actions">
              <button class="icon-button-soft" @click.stop="goto_editor(data.LocationUuid)" title="Edit location">
                <i class="fas fa-pen"></i>
              </button>
            </td>
          </tr>
          <tr v-if="paginatedData.length === 0">
            <td colspan="5">
              <div class="empty-state">
                <strong>No clinics or hospitals found.</strong>
                <p>Create a location or adjust your search.</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination-row" v-if="totalPages > 1">
        <span class="table-secondary">Page {{ current_page }} of {{ totalPages }}</span>
        <div class="table-actions">
          <button class="icon-button-soft" :disabled="current_page === 1" @click="changePage(current_page - 1)">
            <ChevronLeft :size="16" />
          </button>
          <button class="icon-button-soft" :disabled="current_page === totalPages" @click="changePage(current_page + 1)">
            <ChevronRight :size="16" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/Loading.vue";
import SearchBar from "@/components/SearchBar.vue";
import AddClinics from "@/pages/Clinics/components/AddClinics.vue"
import { getAllClinics } from "@/services/clinicServices"
import { getAllOrg } from "@/services/orgServices"
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/authStore'

export default{
  components: {
    Loading,
    SearchBar,
    AddClinics,
    ChevronLeft,
    ChevronRight,
  },

  created(){
    this.get_data();
  },
  async mounted() {
    if (this.authStore.userRole === 'admin') {
      const organizations = await getAllOrg();
      this.org_data = Object.fromEntries(
        (organizations.data || []).map(org => [
          org.OrganizationUuid,
          org.OrganizationName
        ])
      );
    } else {
      await this.authStore.fetchUserDetails();
    }
  },
  data() {
    return {
      authStore: useAuthStore(),
      org_data: {},
      raw_data: [],
      render_data: [],
      current_page: 1,
      page_limit: 10,
      isAddClinics: false,
      isLoading: false,
    }
  },
  computed: {
    paginatedData() {
      const start = (this.current_page - 1) * this.page_limit;
      return this.render_data.slice(start, start + this.page_limit);
    },
    totalPages() {
      return Math.ceil(this.render_data.length / this.page_limit);
    },
    unassignedCount() {
      return this.raw_data.filter((clinic) => !clinic.OrganizationUuid).length;
    },
  },
  methods: {
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.current_page = page;
      }
    },
    organizationName(uuid) {
      if (!uuid) {
        return 'No organization';
      }
      return this.org_data[uuid] || 'Organization linked';
    },
    get_search_query(search_query){
      const query = search_query ? search_query.toLowerCase() : '';
      this.current_page = 1;
      this.render_data = this.raw_data.filter((data) => {
        return [
          data.State,
          data.ClinicName,
          data.City,
          data.Address,
          this.organizationName(data.OrganizationUuid),
        ].some((value) => String(value || '').toLowerCase().includes(query));
      });
    },
    async get_data(){
      this.isLoading = true
      const result = await getAllClinics();

      if(result.data){
        this.raw_data = [...result.data].reverse();
        this.render_data = [...result.data].reverse();
      }
      this.isLoading = false
    },
    goto_editor(input_id){
      this.$router.push({ name: 'clinics-edit', params: { id: input_id} });
    },
    submit_modal(){
      this.isAddClinics = false;
      this.get_data();
    },
    close_modal(){
      this.isAddClinics = false;
    },
    formatDate(date) {
      if (!date) return 'Not updated';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  }
}
</script>

<style scoped>
.clickable-row {
  cursor: pointer;
}

.table-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
